export interface CatalogResearchRow {
  categoryPath: string;
  researchCategoryPath: string | null;
  characteristicsId: string | null;
  hasResearch: boolean;
  researchStatus: string | null;
  lastResearchedAt: string | null;
  researchKeyIsLeaf: boolean;
  personaCount: number | null;
  weightedPersonaCount: number | null;
}

// The endpoint's legacy join can return several research records for one catalog
// path. Only an exact path record supplies the actionable row's research state.
// Keep other records available in the separate, read-only record browser.
export function catalogResearchRows<T extends CatalogResearchRow>(rows: T[]): T[] {
  const groups = new Map<string, T[]>();
  for (const row of rows) groups.set(row.categoryPath, [...(groups.get(row.categoryPath) || []), row]);
  return [...groups.values()].map(group => {
    const exact = group.filter(row => row.researchCategoryPath === row.categoryPath);
    const ids = new Set(exact.map(row => row.characteristicsId).filter(Boolean));
    if (ids.size === 1) return exact[0];
    return { ...group[0], hasResearch:false, researchStatus:ids.size > 1 ? 'ambiguous' : null,
      lastResearchedAt:null, researchCategoryPath:null, characteristicsId:null,
      researchKeyIsLeaf:false, personaCount:null, weightedPersonaCount:null };
  });
}

export interface CatalogSubmission {
  sectorId: number;
  siteName: string;
  requestId: string;
  siteId: string;
  categories: { categoryPath: string; categoryName: string }[];
  researchSteps: string[];
  reenrich: false;
  targetMode: 'catalog_full_path';
}

export function readCatalogSubmission(key: string): CatalogSubmission | null {
  const saved = localStorage.getItem(key);
  if (!saved) return null;
  const record = JSON.parse(saved);
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!record || !Number.isInteger(record.sectorId) || record.sectorId <= 0 || typeof record.siteId !== 'string' || !uuid.test(record.siteId)
    || typeof record.requestId !== 'string' || !uuid.test(record.requestId) || typeof record.siteName !== 'string'
    || !Array.isArray(record.categories) || !record.categories.length
    || !record.categories.every((c: any) => c && typeof c.categoryPath === 'string' && c.categoryPath.trim() && typeof c.categoryName === 'string' && c.categoryName.trim())
    || !Array.isArray(record.researchSteps) || !record.researchSteps.length
    || !record.researchSteps.every((s: any) => ['characteristics','perceived_benefits','canonical','intelligence','extraction_hints'].includes(s))
    || record.reenrich !== false || record.targetMode !== 'catalog_full_path') {
    throw new Error('The saved research request cannot be read. Inspect its job before submitting more research.');
  }
  return record;
}

// History is a bounded navigation index. The server owns complete receipts;
// pending submissions above retain their full idempotent request independently.
export interface ResearchHistoryEntry {
  job: { id: string; siteId: string; sectorId: number; status: string };
}
export function compactResearchHistory(entries: any[]): ResearchHistoryEntry[] {
  const byId = new Map<string, ResearchHistoryEntry>();
  for (const entry of entries) {
    const job = entry?.job;
    if (!job || !['id', 'siteId', 'status'].every(key => typeof job[key] === 'string' && job[key].length > 0 && job[key].length <= 200)
      || !Number.isInteger(job.sectorId) || job.sectorId <= 0
      || !['completed', 'failed', 'cancelled'].includes(job.status)) continue;
    byId.delete(job.id);
    byId.set(job.id, { job: { id: job.id, siteId: job.siteId, sectorId: job.sectorId, status: job.status } });
  }
  return [...byId.values()].slice(-20);
}
